import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { purchaseService, supplierService, api } from '../utils/apiService';
import { calculatePurchaseItemTotals } from '../utils/invoiceCalculations';

/**
 * Custom hook for managing purchase invoice state and operations
 */
const usePurchaseInvoice = (id = null) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(isEditMode); // Start with loading true if editing
  const [productsLoading, setProductsLoading] = useState(true);
  const purchaseLoadedRef = React.useRef(false);
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState([
    { id: 0, name: 'Walk In Supplier', vatNumber: 'N/A', email: 'N/A' }
  ]);
  
  // Products state
  const [products, setProducts] = useState([]);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Invoice state
  const [invoice, setInvoice] = useState({
    invoiceNumber: '',
    date: new Date(),
    supplierInvoiceNo: '',
    supplier: null,
    status: 'On Time',
    paymentStatus: 'Pending',
    notes: '',
    terms: '',
    items: [],
    payments: [],
    paymentMethod: '',
    paymentAmount: '',
    paymentReference: ''
  });
  
  // New item state
  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 1,
    price: 0,
    vatRate: 15,
    discount: 0,
  });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });
  
  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await supplierService.getSuppliers();
        const suppliersData = Array.isArray(response) ? response : 
                             (response?.suppliers || []);
        
        const walkInSupplier = { id: 0, name: 'Walk In Supplier', vatNumber: 'N/A', email: 'N/A' };
        const hasWalkInSupplier = suppliersData.some(supplier => 
          supplier.id === 0 || supplier._id === 0 || 
          supplier.name?.toLowerCase().includes('walk in')
        );
        
        const formattedSuppliers = suppliersData.map(supplier => ({
          id: supplier._id || supplier.id,
          name: supplier.name || 'Unknown Supplier',
          vatNumber: supplier.vatRegistrationNumber || supplier.vatNumber || 'N/A',
          email: supplier.email || 'N/A'
        }));
        
        setSuppliers(hasWalkInSupplier ? formattedSuppliers : [walkInSupplier, ...formattedSuppliers]);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setSuppliers([{ id: 0, name: 'Walk In Supplier', vatNumber: 'N/A', email: 'N/A' }]);
      }
    };
    
    fetchSuppliers();
  }, []);
  
  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const response = await api.get('/products');
        const productsData = response.data.data || response.data;
        
        if (!Array.isArray(productsData)) {
          console.error('Products data is not an array:', productsData);
          return;
        }
        
        const formattedProducts = productsData.map(product => ({
          ...product,
          id: product._id,
          name: product.productName || product.name || '', 
          description: product.description || '',
          price: product.purchaseRate || product.salesRate || 0,
          vat: product.taxRate || product.vatRate || 15,
          vatType: product.taxType || 'standard',
          productCode: product.productCode || '',
          barcode: product.barcode || '',
          hsnCode: product.hsnCode || '',
          unit: product.basicUnit || product.unit || 'EA',
          itemType: product.itemType || 'goods'
        }));
        
        setProducts(formattedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        enqueueSnackbar('Failed to load products', { variant: 'error' });
      } finally {
        setProductsLoading(false);
      }
    };
    
    fetchProducts();
  }, [enqueueSnackbar]);
  
  // Fetch payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await api.get('/masters/payment-methods');
        if (response.data && Array.isArray(response.data)) {
          setPaymentMethods(response.data);
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        // Set default payment methods as fallback
        setPaymentMethods([
          { _id: 'cash_default', name: 'Cash', category: 'cash' },
          { _id: 'card_default', name: 'Credit Card', category: 'card' },
          { _id: 'bank_default', name: 'Bank Transfer', category: 'bank_transfer' }
        ]);
      }
    };
    
    fetchPaymentMethods();
  }, []);
  
  // Fetch purchase data if editing (after suppliers, products, and payment methods are loaded)
  useEffect(() => {
    if (!id || !suppliers.length || !products.length || !paymentMethods.length || purchaseLoadedRef.current) return;
    
    const fetchPurchaseData = async () => {
      try {
        purchaseLoadedRef.current = true;
        const response = await purchaseService.getPurchaseById(id);
        
        // Handle the response structure: { success: true, purchase: {...} }
        const purchaseData = response.purchase || response;
        
        if (!purchaseData?._id) {
          throw new Error('Invalid data received from server');
        }
        
        // Find matching supplier
        let matchedSupplier = null;
        console.log('Purchase data supplierId:', purchaseData.supplierId);
        console.log('Available suppliers:', suppliers);
        
        if (purchaseData.supplierId) {
          // If supplierId is already populated as an object
          if (typeof purchaseData.supplierId === 'object') {
            const supplierId = purchaseData.supplierId._id || purchaseData.supplierId.id;
            
            // Try to find in suppliers list
            matchedSupplier = suppliers.find(s => 
              s.id?.toString() === supplierId?.toString()
            );
            
            // If not found, create from populated data
            if (!matchedSupplier) {
              matchedSupplier = {
                id: supplierId,
                name: purchaseData.supplierId.name || 'Unknown Supplier',
                vatNumber: purchaseData.supplierId.vatRegistrationNumber || 'N/A',
                email: purchaseData.supplierId.email || 'N/A'
              };
            }
          } else {
            // If supplierId is just an ID string
            matchedSupplier = suppliers.find(s => 
              s.id?.toString() === purchaseData.supplierId?.toString()
            );
          }
        }
        
        // Fallback to Walk In Supplier if no supplier found
        if (!matchedSupplier) {
          matchedSupplier = { id: 0, name: 'Walk In Supplier', vatNumber: 'N/A', email: 'N/A' };
        }
        
        console.log('Matched supplier:', matchedSupplier);
        
        // Transform items with product information
        const transformedItems = Array.isArray(purchaseData.details) ? purchaseData.details.map(detail => {
          const product = products.find(p => p.id === detail.productId?._id || p._id === detail.productId?._id);
          return {
            id: detail._id || `temp-${Math.random().toString(36).substring(2, 9)}`,
            product: product || (detail.productId ? {
              id: detail.productId._id || detail.productId,
              name: detail.productId.name || detail.description,
              description: detail.productId.description || detail.description,
              price: detail.unitPrice,
              vat: detail.taxRate || 15,
              unit: detail.productId.unit || detail.unit
            } : null),
            description: detail.description || '',
            quantity: detail.quantity || 0,
            price: detail.unitPrice || 0,
            vatRate: detail.taxRate || 15,
            discount: detail.discountPerc || 0,
            discountAmount: detail.discountAmount || 0,
            unit: detail.unit || '',
            lineTotal: (detail.quantity || 0) * (detail.unitPrice || 0),
            afterDiscount: detail.netValue || 0,
            vatAmount: detail.vatAmt || 0,
            total: detail.totalAmount || detail.grandTotal || 0,
            priceWithVat: (detail.unitPrice || 0) * (1 + (detail.taxRate || 15) / 100)
          };
        }) : [];
        
        const formattedData = {
          _id: purchaseData._id || '',
          invoiceNumber: purchaseData.purchaseNumber || `PUR-${Math.floor(10000 + Math.random() * 90000)}`,
          date: purchaseData.issueDate ? new Date(purchaseData.issueDate) : new Date(),
          supplierInvoiceNo: purchaseData.supplierInvoiceNo || '',
          supplier: matchedSupplier || null,
          status: purchaseData.status || 'draft',
          paymentStatus: purchaseData.paymentStatus || 'Pending',
          notes: purchaseData.notes || '',
          terms: purchaseData.terms || '',
          items: transformedItems,
          payments: Array.isArray(purchaseData.payments) ? purchaseData.payments.map(payment => {
            // Try to find the payment method by ID first, then by name
            const paymentMethodObj = paymentMethods.find(pm => 
              pm._id === payment.method || pm.name === payment.method
            );
            
            return {
              id: payment._id || `temp-${Math.random().toString(36).substring(2, 9)}`,
              date: payment.date ? new Date(payment.date) : new Date(),
              method: paymentMethodObj?.name || payment.method || 'Cash',
              methodId: paymentMethodObj?._id || payment.method,
              category: paymentMethodObj?.category || 'cash',
              amount: payment.amount || 0,
              reference: payment.reference || ''
            };
          }) : []
        };
        
        setInvoice(formattedData);
        enqueueSnackbar('Purchase data loaded successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error fetching purchase data:', error);
        enqueueSnackbar('Error loading purchase data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchPurchaseData();
  }, [id, suppliers.length, products.length, paymentMethods.length, enqueueSnackbar]);
  
  // Fetch next invoice number for new invoices
  useEffect(() => {
    if (id) return;
    
    const fetchNextInvoiceNumber = async () => {
      try {
        const response = await purchaseService.getNextPurchaseNumber();
        if (response?.nextNumber) {
          setInvoice(prev => ({ ...prev, invoiceNumber: response.nextNumber }));
        }
      } catch (error) {
        console.error('Error fetching next invoice number:', error);
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: `PUR-${Math.floor(10000 + Math.random() * 90000)}`
        }));
      }
    };
    
    fetchNextInvoiceNumber();
  }, [id]);
  
  // Calculation functions
  const calculateSubtotal = useCallback((items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  }, []);
  
  const calculateVat = useCallback((items) => {
    // Calculate subtotal first
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);
    
    // Get additional cost and discount
    const additionalCost = parseFloat(invoice.additionalCost) || 0;
    const discountAmount = parseFloat(invoice.discountAmount) || 0;
    
    // Calculate taxable amount (subtotal + additional cost - discount)
    const taxableAmount = subtotal + additionalCost - discountAmount;
    
    // Calculate VAT on taxable amount proportionally based on item VAT rates
    const totalItemsValue = subtotal;
    
    return items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.price;
      // Calculate this item's proportion of the total
      const itemProportion = totalItemsValue > 0 ? itemTotal / totalItemsValue : 0;
      // Apply discount and additional cost proportionally to this item
      const itemTaxableAmount = (itemTotal + (additionalCost * itemProportion)) - (discountAmount * itemProportion);
      // Calculate VAT on the taxable amount for this item
      const itemVat = (itemTaxableAmount * (item.vatRate / 100));
      return sum + itemVat;
    }, 0);
  }, [invoice.additionalCost, invoice.discountAmount]);
  
  const calculateTotal = useCallback((items) => {
    const subtotal = calculateSubtotal(items);
    const vat = calculateVat(items);
    const additionalCost = parseFloat(invoice.additionalCost) || 0;
    const discountAmount = parseFloat(invoice.discountAmount) || 0;
    const taxableAmount = subtotal + additionalCost - discountAmount;
    
    return taxableAmount + vat;
  }, [calculateSubtotal, calculateVat, invoice.additionalCost, invoice.discountAmount]);
  
  const getTotalPaid = useCallback(() => {
    return invoice.payments.reduce((total, payment) => total + Number(payment.amount), 0);
  }, [invoice.payments]);
  
  const getRemainingBalance = useCallback(() => {
    const totalAmount = calculateTotal(invoice.items);
    const totalPaid = getTotalPaid();
    return Math.max(0, totalAmount - totalPaid);
  }, [invoice.items, calculateTotal, getTotalPaid]);
  
  // Item handlers
  const handleItemChange = useCallback((itemId, field, value) => {
    if (['quantity', 'price', 'discount', 'vatRate', 'discountAmount'].includes(field)) {
      value = Number(value);
    }
    
    setInvoice(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Handle unit conversion - update price based on selected unit
          if (field === 'unit' && item.product) {
            if (value === item.product.basicUnit) {
              updatedItem.price = item.product.purchaseRate || item.product.price || 0;
            } else if (value === item.product.alternateUnit) {
              updatedItem.price = item.product.altPurchaseRate || 0;
            }
          }
          
          // Pass the field name to support bidirectional discount calculation
          return { ...updatedItem, ...calculatePurchaseItemTotals(updatedItem, field) };
        }
        return item;
      });
      
      return { ...prev, items: updatedItems };
    });
  }, []);
  
  // Handle new item changes with calculations
  const handleNewItemChange = useCallback((field, value) => {
    if (['quantity', 'price', 'discount', 'vatRate', 'discountAmount'].includes(field)) {
      value = Number(value);
    }
    
    setNewItem(prev => {
      const updatedItem = { ...prev, [field]: value };
      
      // Handle unit conversion - update price based on selected unit
      if (field === 'unit' && prev.product) {
        if (value === prev.product.basicUnit) {
          updatedItem.price = prev.product.purchaseRate || prev.product.price || 0;
        } else if (value === prev.product.alternateUnit) {
          updatedItem.price = prev.product.altPurchaseRate || 0;
        }
      }
      
      // Calculate totals for preview using purchase calculation
      // Pass the field name to support bidirectional discount calculation
      const calculated = calculatePurchaseItemTotals(updatedItem, field);
      return { ...updatedItem, ...calculated };
    });
  }, []);
  
  const handleAddItem = useCallback(() => {
    if (!newItem.product || newItem.quantity <= 0 || newItem.price <= 0) {
      enqueueSnackbar('Please select a product and enter quantity and price', { variant: 'warning' });
      return;
    }
    
    const itemToAdd = {
      id: invoice.items.length + 1,
      product: newItem.product,
      description: newItem.description || newItem.product.description || '',
      quantity: newItem.quantity,
      price: newItem.price,
      vatRate: newItem.vatRate,
      discount: newItem.discount,
      unit: newItem.unit || newItem.product.unit || 'EA',
      ...calculatePurchaseItemTotals(newItem)
    };
    
    setInvoice(prev => ({ ...prev, items: [...prev.items, itemToAdd] }));
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
      unit: 'EA'
    });
  }, [newItem, invoice.items, enqueueSnackbar]);
  
  const handleRemoveItem = useCallback((itemId) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  }, []);
  
  // Payment handlers
  const handleAddPayment = useCallback(() => {
    if (!invoice.paymentAmount || !invoice.paymentMethod) {
      enqueueSnackbar('Please enter payment amount and select payment method', { variant: 'warning' });
      return;
    }
    
    const payment = {
      id: invoice.payments.length + 1,
      date: new Date(),
      amount: Number(invoice.paymentAmount),
      method: invoice.paymentMethod,
      reference: invoice.paymentReference
    };
    
    const newTotalPaid = getTotalPaid() + Number(invoice.paymentAmount);
    const totalAmount = calculateTotal(invoice.items);
    
    let newPaymentStatus = 'Not Paid';
    if (newTotalPaid >= totalAmount) {
      newPaymentStatus = 'Paid';
    } else if (newTotalPaid > 0) {
      newPaymentStatus = 'Partially Paid';
    }
    
    setInvoice(prev => ({
      ...prev,
      payments: [...prev.payments, payment],
      paymentAmount: '',
      paymentReference: '',
      paymentStatus: newPaymentStatus
    }));
  }, [invoice, getTotalPaid, calculateTotal, enqueueSnackbar]);
  
  const handleRemovePayment = useCallback((paymentId) => {
    const paymentToRemove = invoice.payments.find(p => p.id === paymentId);
    const newTotalPaid = getTotalPaid() - paymentToRemove.amount;
    const totalAmount = calculateTotal(invoice.items);
    
    let newPaymentStatus = 'Not Paid';
    if (newTotalPaid >= totalAmount) {
      newPaymentStatus = 'Paid';
    } else if (newTotalPaid > 0) {
      newPaymentStatus = 'Partially Paid';
    }
    
    setInvoice(prev => ({
      ...prev,
      payments: prev.payments.filter(payment => payment.id !== paymentId),
      paymentStatus: newPaymentStatus
    }));
  }, [invoice.payments, invoice.items, getTotalPaid, calculateTotal]);
  
  // Submit invoice
  const submitInvoice = useCallback(async (status = 'draft') => {
    try {
      if (invoice.items.length === 0) {
        enqueueSnackbar('Please add at least one item to the invoice', { variant: 'warning' });
        return;
      }
      
      setLoading(true);
      
      const subtotal = calculateSubtotal(invoice.items);
      const taxAmount = calculateVat(invoice.items);
      const totalAmount = calculateTotal(invoice.items);
      const paidAmount = getTotalPaid();
      const balanceAmount = totalAmount - paidAmount;
      
      const purchaseData = {
        master: {
          supplierId: invoice.supplier?.id || null,
          issueDate: invoice.date.toISOString(),
          supplierInvoiceNo: invoice.supplierInvoiceNo || '',
          subtotal,
          taxAmount,
          totalAmount,
          status: status === 'submitted' ? 'Submitted' : status === 'draft' ? 'Draft' : 'Submitted',
          notes: invoice.notes || '',
          terms: invoice.terms || '',
          additionalCost: parseFloat(invoice.additionalCost) || 0,
          discountPerc: parseFloat(invoice.discountPercentage) || 0,
          discountAmt: parseFloat(invoice.discountAmount) || 0,
          paidAmt: paidAmount,
          balanceAmt: balanceAmount,
          paymentStatus: paidAmount >= totalAmount ? 'Paid' : paidAmount > 0 ? 'Partially Paid' : 'Not Paid'
        },
        details: invoice.items.map(item => ({
          productId: item.product?.id || item.product?._id || null,
          description: item.description || '',
          quantity: Number(item.quantity) || 0,
          unit: item.unit || 'EA',
          unitPrice: Number(item.price) || 0,
          taxRate: Number(item.vatRate) || 0,
          discountType: item.discountType || 'percentage',
          discountValue: Number(item.discount) || 0,
          notes: item.notes || ''
        })),
        payments: invoice.payments.map(payment => ({
          date: payment.date.toISOString(),
          amount: Number(payment.amount) || 0,
          method: payment.method || 'Cash',
          reference: payment.reference || '',
          notes: payment.notes || '',
          formId: 1
        }))
      };
      
      const response = id 
        ? await purchaseService.updatePurchase(id, purchaseData)
        : await purchaseService.createPurchase(purchaseData);
      
      enqueueSnackbar(`Purchase invoice ${id ? 'updated' : 'created'} successfully`, { variant: 'success' });
      return response;
    } catch (error) {
      console.error('Error submitting purchase invoice:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error creating purchase invoice', { variant: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [invoice, id, calculateSubtotal, calculateVat, calculateTotal, getTotalPaid, enqueueSnackbar]);
  
  return {
    // State
    invoice,
    setInvoice,
    newItem,
    setNewItem,
    visibleColumns,
    setVisibleColumns,
    suppliers,
    products,
    loading,
    productsLoading,
    
    // Calculations
    calculateSubtotal,
    calculateVat,
    calculateTotal,
    getTotalPaid,
    getRemainingBalance,
    
    // Handlers
    handleItemChange,
    handleNewItemChange,
    handleAddItem,
    handleRemoveItem,
    handleAddPayment,
    handleRemovePayment,
    submitInvoice
  };
};

export default usePurchaseInvoice;
